import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import X from 'components/lib/icons/x_icon';

// Documentation: http://go.glad.ly/style/#/Common%20Components/Chip

export default function Chip({ disabled, onDelete, onClick, label, accent, small, ...props }) {
  let ChipRender = disabled ? ChipDisabled : accent ? ChipAccent : ChipMain;

  return (
    <ChipRender hasDelete={!!onDelete} onClick={!disabled ? onClick : undefined} small={small} {...props}>
      <ChipText small={small}>{label}</ChipText>
      {onDelete && (
        <IconWrapper
          data-aid="chip-delete-button"
          disabled={disabled}
          hasAccent={!!accent}
          hasClick={!!onClick}
          onClick={!disabled ? onDelete : undefined}
          small={small}
        >
          <CloseIcon $small={small} disabled={disabled} />
        </IconWrapper>
      )}
    </ChipRender>
  );
}

export const ChipText = styled.div`
  align-items: center;
  align-self: flex-start;
  cursor: ${p => (p.onClick ? 'pointer' : 'default')};
  display: block;
  /* Must shrink: the delete icon is in flow, so a label that refuses to give up
     width pushes it outside the pill in width-capped consumers. */
  flex: 1 1 auto;
  font-size: ${p => (p.small ? p.theme.fontSize.small : p.theme.fontSize.base)};
  font-weight: ${p => p.theme.fontWeight.medium};
  line-height: ${p => p.theme.lineHeight.controls};
  min-width: 0px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all ${p => p.theme.transitions.fast};
  white-space: nowrap;
`;

// The delete icon is in flow rather than an absolute overlay, so the right
// padding no longer reserves room for it.
function chipPadding({ small, hasDelete }) {
  if (small) {
    return hasDelete ? '3px 6px 3px 10px' : '3px 10px';
  }
  return hasDelete ? '8px 10px 8px 16px' : '8px 16px';
}

const chipBase = css`
  align-items: center;
  border: 1px solid;
  box-shadow: ${p => p.theme.boxShadow.xsmall};
  border-radius: 50px;
  display: flex;
  flex-direction: row;
  padding: ${chipPadding};
  position: relative;
  max-height: ${p => (p.small ? '24px' : '34px')};
  max-width: 100%;
`;

export const ChipDisabled = styled.div`
  ${chipBase};
  border-color: ${p => p.theme.colors.gray300};
  background: ${p => p.theme.colors.gray100};
  color: ${p => p.theme.colors.gray300};
  stroke: ${p => p.theme.colors.gray300};
`;

export const ChipMain = styled.div`
  ${chipBase};
  border-color: ${p => p.theme.colors.gray300};
  background: ${p => p.theme.colors.white};
  ${p => p.onClick && ChipMainInteraction};
`;

const ChipMainInteraction = css`
  &:hover {
    background: ${p => p.theme.colors.gray100};
  }
  &:active {
    background: ${p => p.theme.colors.gray600};
    color: ${p => p.theme.colors.white};
  }
`;

export const ChipAccent = styled.div`
  ${chipBase};
  background: ${p => p.theme.colors.purple400};
  border-color: ${p => p.theme.colors.purple400};
  color: ${p => p.theme.colors.white};
  ${p => p.onClick && ChipAccentInteraction};
`;

const ChipAccentInteraction = css`
  &:hover {
    background: ${p => p.theme.colors.purple500};
    border-color: ${p => p.theme.colors.purple500};
  }
  &:active {
    background: ${p => p.theme.colors.purple700};
    border-color: ${p => p.theme.colors.purple700};
  }
`;

export const IconWrapper = styled.div`
  align-items: center;
  background: transparent;
  mix-blend-mode: luminosity;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  height: ${p => (p.small ? '16px' : '22px')};
  justify-content: center;
  flex: 0 0 auto;
  line-height: 0;
  margin: 0px 0px 0px 4px;
  stroke: ${p => (p.hasAccent ? p.theme.colors.white : p.theme.colors.black)};
  transition: all ${p => p.theme.transitions.fast};
  width: ${p => (p.small ? '16px' : '20px')};

  ${ChipMain} > &:hover {
    background: ${p => p.theme.colors.gray300};
  }
  ${ChipMain} > &:active {
    background: ${p => p.theme.colors.gray400};
  }
  /* When the chip is active, make the "X" white because it looks bad black  */
  ${ChipMain}:active > & {
    stroke: ${p => (p.hasClick ? p.theme.colors.white : p.theme.colors.black)};
  }
  ${ChipAccent} > &:hover {
    background: ${p => p.theme.colors.gray800};
  }
  ${ChipAccent} > &:active {
    background: ${p => p.theme.colors.gray900};
  }
  &[disabled] {
    stroke: ${p => p.theme.colors.gray300};
    background: transparent;
    cursor: default;
  }
  &[disabled]:hover {
    stroke: ${p => p.theme.colors.gray300};
    background: transparent;
    cursor: default;
  }
  &[disabled]:active {
    stroke: ${p => p.theme.colors.gray300};
    background: transparent;
    cursor: default;
  }
`;

// Transient prop: CloseIcon wraps a component target, so styled-components
// forwards everything down to the underlying <svg> unless the name is prefixed.
const CloseIcon = styled(X)`
  width: ${p => (p.$small ? '9px' : '12px')};
  height: ${p => (p.$small ? '9px' : '12px')};
`;

Chip.propTypes = {
  /** Accent (purple filled) variant */
  accent: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  /** Determines which side the inline spacing will be applied */
  direction: PropTypes.oneOf(['left', 'right']),
  /** Click handler for entire chip. This gives the chip interactive (hover, active) styles */
  onClick: PropTypes.func,
  /** Text to display within the chip */
  label: PropTypes.string.isRequired,
  /** Function to be called when the remove "X" icon is clicked. The presence of this function will create the "X" icon on the chip */
  onDelete: PropTypes.func,
  /** Renders a more compact chip (smaller height, padding, and font) */
  small: PropTypes.bool,
};

Chip.defaultProps = {
  inline: 'small',
};
